// ---------------------------------------------------------------------------
// Test Mode — simulated bank reconciliation dataset (CLE-264).
//
// A realistic Romanian bank statement (RON) with ~26 transactions: client
// incassations, supplier payments, salaries, rent and subscriptions. Each line
// carries an AI-style reconciliation status + confidence score so the
// bank-statement pages in Test Mode (and the landing-page screenshot for
// CLE-262) renders the full "upload extras → AI matching" experience without a
// live backend.
//
// Distribution roughly: 60% AUTO_MATCHED, 25% MATCHED, 15% UNMATCHED, with a
// couple of CONFIRMED lines to show the manual-confirm state.
// ---------------------------------------------------------------------------

import { isoDate, isoDateTime } from './company';
import type { ReconCandidatesDto } from '../lib/bankStatementsApi';
import { CLIENT_SEEDS } from './clients';
import { SUPPLIER_SEEDS } from './suppliers';
import { PEOPLE_REFS } from './people';
import { PROJECT_SEEDS } from './projects';

export type ReconStatus = 'MATCHED' | 'UNMATCHED' | 'AUTO_MATCHED' | 'CONFIRMED';
export type TxnType = 'credit' | 'debit';

export interface ReconTransaction {
  id: string;
  date: string;
  description: string;
  /** Signed amount in RON: positive = credit (incassation), negative = debit. */
  amount: number;
  type: TxnType;
  status: ReconStatus;
  /** Matched invoice number, when a counterpart was found. */
  matchedInvoice: string | null;
  /** AI confidence 0–100 for AUTO_MATCHED / MATCHED rows; null when unmatched. */
  confidence: number | null;
}

/** A candidate invoice surfaced in the manual-matching side panel. */
export interface InvoiceCandidate {
  id: string;
  number: string;
  partner: string;
  amount: number;
  /** Similarity 0–100 against the selected transaction. */
  similarity: number;
}

export interface BankStatementMeta {
  bankName: string;
  iban: string;
  accountHolder: string;
  periodStart: string;
  periodEnd: string;
  fileName: string;
}

export const RECON_STATEMENT_META: BankStatementMeta = {
  bankName: 'Banca Transilvania',
  iban: 'RO49 BTRL 0123 4567 8901 2345',
  accountHolder: 'CLEAN CODE CULTURE SRL',
  periodStart: isoDate(-30),
  periodEnd: isoDate(-1),
  fileName: 'Extras_BT_2025-05.pdf',
};

// Raw seed rows. `day` is the offset (negative = days ago) used to spread the
// transactions across the statement period.
interface Seed {
  day: number;
  description: string;
  amount: number;
  status: ReconStatus;
  matchedInvoice: string | null;
  confidence: number | null;
}

const SEEDS: Seed[] = [
  // --- Client incassations (credit) -----------------------------------------
  {
    day: -29,
    description: 'INCASARE FashionRO SRL - Factura TF-1001',
    amount: 28560,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'TF-1001',
    confidence: 98,
  },
  {
    day: -27,
    description: 'TRANSFER FinTech Partners SA / FACT TF-1014',
    amount: 47600,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'TF-1014',
    confidence: 95,
  },
  {
    day: -25,
    description: 'OP DataViz Corp SRL fact 1021',
    amount: 19040,
    status: 'MATCHED',
    matchedInvoice: 'TF-1021',
    confidence: 99,
  },
  {
    day: -23,
    description: 'INCASARE MEGA INDUSTRIES - plata partiala',
    amount: 33320,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'TF-1028',
    confidence: 81,
  },
  {
    day: -21,
    description: 'TRANSF LogiTrans SA ref 2025-0044',
    amount: 23800,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'TF-1033',
    confidence: 72,
  },
  {
    day: -18,
    description: 'INCASARE TRANSFER - ref necunoscuta 884512',
    amount: 11900,
    status: 'UNMATCHED',
    matchedInvoice: null,
    confidence: null,
  },
  {
    day: -16,
    description: 'OP HealthSync SRL Factura TF-1041',
    amount: 35700,
    status: 'CONFIRMED',
    matchedInvoice: 'TF-1041',
    confidence: 100,
  },
  {
    day: -14,
    description: 'INCASARE EduPlatform SRL fct TF-1047',
    amount: 16660,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'TF-1047',
    confidence: 90,
  },
  {
    day: -11,
    description: 'TRANSFER RetailHub SRL 1052',
    amount: 21420,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'TF-1052',
    confidence: 88,
  },
  {
    day: -9,
    description: 'INCASARE SmartFactory SRL - avans proiect',
    amount: 26180,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'TF-1058',
    confidence: 76,
  },
  {
    day: -6,
    description: 'INCASARE - virament intern fonduri 99021',
    amount: 8000,
    status: 'UNMATCHED',
    matchedInvoice: null,
    confidence: null,
  },
  {
    day: -3,
    description: 'OP GovTech România SA fact TF-1063',
    amount: 53550,
    status: 'MATCHED',
    matchedInvoice: 'TF-1063',
    confidence: 97,
  },

  // --- Supplier payments (debit) --------------------------------------------
  {
    day: -28,
    description: 'PLATA AWS ROMANIA - cloud hosting',
    amount: -4280,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'AWS-2025-05',
    confidence: 94,
  },
  {
    day: -26,
    description: 'PLATA ContaPlus SRL - servicii contabilitate',
    amount: -2500,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'CP-0521',
    confidence: 92,
  },
  {
    day: -24,
    description: 'ABONAMENT JetBrains / Figma / Slack / GitHub',
    amount: -1840,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'SaaS-0512',
    confidence: 85,
  },
  {
    day: -22,
    description: 'PLATA CHIRIE BIROU - Cowork Cluj',
    amount: -7500,
    status: 'MATCHED',
    matchedInvoice: 'CWK-0501',
    confidence: 99,
  },
  {
    day: -20,
    description: 'PLATA Orange Business - abonament telefonie',
    amount: -960,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'ORG-0518',
    confidence: 89,
  },
  {
    day: -17,
    description: 'PLATA DigitalAds SRL - campanie marketing',
    amount: -6200,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'DA-0509',
    confidence: 67,
  },
  {
    day: -15,
    description: 'PLATA FAN Courier - livrari',
    amount: -340,
    status: 'UNMATCHED',
    matchedInvoice: null,
    confidence: null,
  },
  {
    day: -12,
    description: 'PLATA Allianz - asigurare raspundere profesionala',
    amount: -1450,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'ALZ-0510',
    confidence: 78,
  },
  {
    day: -8,
    description: 'PLATA POS - achizitie echipamente IT',
    amount: -3890,
    status: 'UNMATCHED',
    matchedInvoice: null,
    confidence: null,
  },
  {
    day: -5,
    description: 'PLATA Avocații Asociați - consultanta juridica',
    amount: -2200,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'AVA-0506',
    confidence: 71,
  },

  // --- Salaries & taxes (debit) ---------------------------------------------
  {
    day: -25,
    description: 'PLATA SALARII - virament colectiv state plata',
    amount: -68400,
    status: 'CONFIRMED',
    matchedInvoice: 'SAL-2025-05',
    confidence: 100,
  },
  {
    day: -25,
    description: 'PLATA CONTRIBUTII - ANAF impozite si taxe salariale',
    amount: -31200,
    status: 'AUTO_MATCHED',
    matchedInvoice: 'ANAF-0525',
    confidence: 96,
  },
  {
    day: -10,
    description: 'PLATA TVA - ANAF declaratie 300',
    amount: -14800,
    status: 'MATCHED',
    matchedInvoice: 'TVA-0510',
    confidence: 93,
  },
];

let cached: ReconTransaction[] | null = null;

/** Build the simulated statement transactions (memoised). */
export function buildReconTransactions(): ReconTransaction[] {
  if (cached) return cached;
  cached = SEEDS.map((s, i) => ({
    id: `recon-txn-${i + 1}`,
    date: isoDate(s.day),
    description: s.description,
    amount: s.amount,
    type: s.amount >= 0 ? ('credit' as const) : ('debit' as const),
    status: s.status,
    matchedInvoice: s.matchedInvoice,
    confidence: s.confidence,
  })).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return cached;
}

// Candidate invoices used by the manual-matching panel. Sorted by similarity
// by the consumer; we provide a plausible spread per transaction id below.
const CANDIDATE_POOL: Array<Omit<InvoiceCandidate, 'similarity'>> = [
  { id: 'cand-1', number: 'TF-1035', partner: 'LogiTrans SA', amount: 11900 },
  { id: 'cand-2', number: 'TF-1039', partner: 'AgroTech SA', amount: 12180 },
  { id: 'cand-3', number: 'TF-1044', partner: 'PeopleFirst SRL', amount: 11550 },
  { id: 'cand-4', number: 'TF-1050', partner: 'DataViz Corp SRL', amount: 8400 },
  { id: 'cand-5', number: 'TF-1056', partner: 'RetailHub SRL', amount: 7820 },
  { id: 'cand-6', number: 'FAN-0515', partner: 'FAN Courier', amount: 340 },
];

/**
 * Deterministic candidate list for an UNMATCHED transaction, sorted by a
 * pseudo-similarity that biases toward amount closeness so the panel feels
 * real. Always returns 3–4 candidates.
 */
export function candidatesFor(txn: ReconTransaction): InvoiceCandidate[] {
  const target = Math.abs(txn.amount);
  return CANDIDATE_POOL.map((c) => {
    const diff = Math.abs(c.amount - target);
    const closeness = Math.max(0, 100 - Math.round((diff / Math.max(target, 1)) * 120));
    // Keep scores in a believable 40–95 band.
    const similarity = Math.min(95, Math.max(41, closeness));
    return { ...c, similarity };
  })
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, 4);
}

// ---------------------------------------------------------------------------
// Bank statement summaries & detail for GET /api/bank-statements (demo audit).
// ---------------------------------------------------------------------------

// Matches StatementSummary from lib/bankStatementsApi.ts.
import type { StatementSummary } from '../lib/bankStatementsApi';

export type BankStatementSummary = StatementSummary;

export function buildBankStatementSummaries(): StatementSummary[] {
  return [
    {
      id: 'bs-1',
      companyId: null,
      company: null,
      bankAccountId: 'ba-1',
      bankAccount: {
        id: 'ba-1',
        iban: 'RO49BTRL0123456789012345',
        label: 'Cont principal RON',
        bankName: 'Banca Transilvania',
      },
      bankName: 'Banca Transilvania',
      bankCode: 'BTRL',
      iban: 'RO49 BTRL 0123 4567 8901 2345',
      accountHolder: 'CLEAN CODE CULTURE SRL',
      periodStart: isoDate(-60),
      periodEnd: isoDate(-31),
      openingBalance: 142000,
      closingBalance: 285000,
      // Header totals slightly off vs the mock lines — demos the amber
      // "Diferență față de extras" tie-out badge on the reconcile footer.
      totalCredits: 325800,
      totalDebits: 145560,
      status: 'RECONCILED',
      originalFilename: 'Extras_BT_luna_anterioara.pdf',
      transactionCount: 24,
      parsedAt: isoDateTime(-28, 10),
      reconciledAt: isoDateTime(-27, 14),
      createdAt: isoDateTime(-28, 10),
    },
    {
      id: 'bs-2',
      companyId: null,
      company: null,
      bankAccountId: 'ba-1',
      bankAccount: {
        id: 'ba-1',
        iban: 'RO49BTRL0123456789012345',
        label: 'Cont principal RON',
        bankName: 'Banca Transilvania',
      },
      bankName: 'Banca Transilvania',
      bankCode: 'BTRL',
      iban: 'RO49 BTRL 0123 4567 8901 2345',
      accountHolder: 'CLEAN CODE CULTURE SRL',
      periodStart: isoDate(-30),
      periodEnd: isoDate(-1),
      openingBalance: 285000,
      closingBalance: 465170,
      // Exactly the sum of the mock transactions — demos the green "✓
      // Corespunde extrasului" tie-out badge.
      totalCredits: 325730,
      totalDebits: 145560,
      status: 'PARTIALLY_RECONCILED',
      originalFilename: 'Extras_BT_luna_curenta.pdf',
      transactionCount: 26,
      parsedAt: isoDateTime(-1, 9),
      reconciledAt: null,
      createdAt: isoDateTime(-1, 9),
    },
    {
      id: 'bs-3',
      companyId: 'co-2',
      company: { id: 'co-2', name: 'Firmio Studio SRL' },
      bankAccountId: 'ba-3',
      bankAccount: {
        id: 'ba-3',
        iban: 'RO49INGB0001000100010001',
        label: 'Cont ING RON',
        bankName: 'ING Bank',
      },
      bankName: 'ING Bank',
      bankCode: 'INGB',
      iban: 'RO49 INGB 0001 0001 0001 0001',
      accountHolder: 'FIRMIO STUDIO SRL',
      periodStart: isoDate(-30),
      periodEnd: isoDate(-1),
      openingBalance: 48000,
      closingBalance: 95000,
      totalCredits: 91000,
      totalDebits: 44000,
      status: 'DRAFT',
      originalFilename: 'Extras_ING_iunie.pdf',
      transactionCount: 14,
      parsedAt: isoDateTime(0, 8),
      reconciledAt: null,
      createdAt: isoDateTime(0, 8),
    },
  ];
}

import type {
  StatementTransaction,
  StatementDetail,
  ReconStatus as ApiReconStatus,
  ReconConfidence,
  ReconType,
} from '../lib/bankStatementsApi';

/** Map local ReconStatus → API ReconStatus. */
function mapStatus(local: ReconStatus): ApiReconStatus {
  if (local === 'MATCHED' || local === 'CONFIRMED') return 'MANUAL_MATCHED';
  if (local === 'AUTO_MATCHED') return 'AUTO_MATCHED';
  return 'UNMATCHED';
}

/** Map simplified ReconTransaction[] to full StatementTransaction[] for the reconcile page. */
function toStatementTransactions(): StatementTransaction[] {
  const recon = buildReconTransactions();
  return recon.map((t) => {
    const apiStatus = mapStatus(t.status);
    const confidence: ReconConfidence | null =
      t.confidence != null
        ? t.confidence >= 80
          ? 'HIGH'
          : t.confidence >= 50
            ? 'MEDIUM'
            : 'LOW'
        : null;
    const reconType: ReconType | null =
      t.type === 'credit' ? 'INCOME' : t.type === 'debit' ? 'OUTCOME' : null;
    return {
      id: t.id,
      date: t.date,
      processingDate: null,
      counterparty:
        t.description.split(' - ')[0]?.replace(/^(INCASARE|TRANSFER|TRANSF|OP)\s+/i, '') ?? null,
      counterpartyIban: null,
      counterpartyBank: null,
      counterpartyCui: null,
      title: t.matchedInvoice
        ? `${t.description.split(' ')[1] ?? 'Tranzacție'} — ${t.matchedInvoice}`
        : null,
      description: t.description,
      type: t.type,
      amount: Math.abs(t.amount),
      currency: 'RON',
      originalAmount: null,
      originalCurrency: null,
      exchangeRate: null,
      bankReference: `REF-${t.id}`,
      balance: null,
      invoiceReference: t.matchedInvoice,
      paymentMethod: 'BANK_TRANSFER' as const,
      isDuplicate: false,
      duplicateVerdict: null,
      originalTransactionId: null,
      isInternalTransfer: false,
      internalTransferAccountId: null,
      reconciliationStatus: apiStatus,
      reconciliationConfidence: confidence,
      reconciliationType: t.matchedInvoice ? reconType : null,
      matchedClientId: null,
      matchedSupplierId: null,
      matchedPersonId: null,
      matchedProjectId: null,
      matchedInvoiceId: t.matchedInvoice ? `inv-${t.matchedInvoice}` : null,
      allocations: [],
      reconciliationSource: null,
      importBatchId: null,
      matchedSubscriptionId: null,
      matchedServiceId: null,
      expenseCategoryId: null,
      matchReason: t.confidence != null ? `AI match (${t.confidence}%)` : null,
      suggestion: null,
      administratorMatch: null,
    };
  });
}

export function buildBankStatementDetail(id: string): StatementDetail {
  const summaries = buildBankStatementSummaries();
  const summary = summaries.find((s) => s.id === id) ?? summaries[1];
  return {
    ...summary,
    transactions: toStatementTransactions(),
    pdfUrl: null,
  };
}

// ---------------------------------------------------------------------------
// Statement-agnostic master data for GET /api/bank-statements/candidates,
// consumed by the "Adaugă plată cash" modal in Test Mode.
// ---------------------------------------------------------------------------

/** Master-data candidates (clients/suppliers/people/categories/projects). */
export function buildReconCandidates(): ReconCandidatesDto {
  return {
    clients: CLIENT_SEEDS.map((c) => ({ id: c.id, name: c.name })),
    suppliers: SUPPLIER_SEEDS.map((s) => ({
      id: s.id,
      name: s.name,
      isRecurring: s.isRecurring,
      expenseCategoryId: null,
    })),
    people: PEOPLE_REFS.map((p) => ({ id: p.id, name: p.name })),
    invoices: [],
    categories: [
      { id: 'cat-software', code: 'software', name: 'Software și licențe' },
      { id: 'cat-office', code: 'office', name: 'Birou și utilități' },
      { id: 'cat-travel', code: 'travel', name: 'Deplasări' },
      { id: 'cat-marketing', code: 'marketing', name: 'Marketing' },
      { id: 'cat-altele', code: 'altele', name: 'Altele' },
      {
        id: 'cat-imprumut-admin',
        code: 'imprumut_administrator',
        name: 'Împrumut administrator',
      },
      {
        id: 'cat-restituire-imprumut-admin',
        code: 'restituire_imprumut_administrator',
        name: 'Restituire împrumut administrator',
      },
    ],
    projects: PROJECT_SEEDS.map((p) => ({ id: p.id, name: p.name, code: p.code })),
    subscriptions: [
      {
        id: 'sub-claude',
        name: 'ANTHROPIC* CLAUDE SUB',
        category: 'saas',
        supplierId: null,
        expenseCategoryId: null,
      },
      {
        id: 'sub-slack',
        name: 'SLACK',
        category: 'saas',
        supplierId: null,
        expenseCategoryId: null,
      },
      {
        id: 'sub-vodafone',
        name: 'PayU*vodafone.ro',
        category: 'telecom',
        supplierId: null,
        expenseCategoryId: null,
      },
    ],
  };
}
